import { Form, Input, TooltipProps } from 'antd';
import React, { ComponentProps, ReactElement, ReactNode } from 'react';
import { Control, Controller as ControllerHookForm } from 'react-hook-form';

import { toFieldStatus } from 'src/app/utils/toFieldStatus';

const { Item: FormItem } = Form;

const formatCurrency = (value: string, allowZero = false): string => {
  if (!value) return '';

  const numbers = value.replaceAll(/\D/g, '');
  if (!numbers) return '';

  // Mantem compatibilidade: sem allowZero, zeros continuam vazios
  if (Number.parseInt(numbers) === 0) {
    return allowZero ? '0,00' : '';
  }

  const padded = numbers.padStart(3, '0');
  const cents = padded.slice(-2);
  let integer = padded.slice(0, -2) || '0';

  // Remove zeros à esquerda, mas mantém pelo menos um zero
  integer = integer.replace(/^0+/, '') || '0';

  const formattedInteger = integer.replaceAll(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formattedInteger},${cents}`;
};

const parseCurrencyValue = (value: string): string => {
  if (!value) return '';
  return value.replaceAll('.', '').replace(',', '.');
};

interface CurrencyControllerProps extends ComponentProps<typeof Input> {
  control?: Control<any>;
  name: string;
  placeholder?: string;
  defaultValue?: string;
  onClick?: () => void;
  onValueChange?: (value: string) => void;
  label?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
  tooltip?: ReactNode | (TooltipProps & { icon?: ReactElement });
  reserveErrorSpace?: boolean;
  allowZero?: boolean;
}

export const CurrencyController: React.FC<CurrencyControllerProps> = ({
  control,
  defaultValue,
  onValueChange,
  name,
  placeholder = '0,00',
  label,
  required,
  maxLength,
  disabled,
  tooltip,
  reserveErrorSpace,
  allowZero = false,
  ...rest
}) => {
  return (
    <ControllerHookForm
      control={control}
      name={name}
      defaultValue={defaultValue ?? undefined}
      render={({ field, fieldState }) => {
        const { status, help } = toFieldStatus(fieldState.error);

        return (
          <FormItem
            required={required}
            validateStatus={status}
            help={help || (reserveErrorSpace ? ' ' : undefined)}
            label={label}
            {...(tooltip && { tooltip: tooltip })}
          >
            <Input
              {...rest}
              {...field}
              onChange={e => {
                const inputValue = e.target.value.replaceAll(/\D/g, '');

                // Se não há números, define como vazio
                if (!inputValue) {
                  field.onChange('');
                  if (onValueChange) {
                    onValueChange('');
                  }
                  e.target.value = '';
                  return;
                }

                // Mantem compatibilidade para campos que não aceitam zero
                if (!allowZero && Number.parseInt(inputValue) === 0) {
                  field.onChange('');
                  if (onValueChange) {
                    onValueChange('');
                  }
                  e.target.value = '';
                  return;
                }

                const formattedValue = formatCurrency(
                  e.target.value,
                  allowZero
                );
                const rawValue = parseCurrencyValue(formattedValue);

                field.onChange(rawValue);

                if (onValueChange) {
                  onValueChange(rawValue);
                }
                e.target.value = formattedValue;
              }}
              value={field.value ? formatCurrency(field.value, allowZero) : ''}
              name={name}
              aria-label={label}
              placeholder={placeholder}
              data-testid={name}
              type={'text'}
              autoComplete={'off'}
              prefix={'R$'}
              disabled={disabled}
              {...(maxLength && { maxLength: maxLength })}
              showCount={false}
            />
          </FormItem>
        );
      }}
    />
  );
};
